import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function GlobalLoader() {
  const [activeRequests, setActiveRequests] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let progressTimer = null;
    let fadeOutTimer = null;

    if (activeRequests > 0) {
      setVisible(true);
      // Start or reset progress
      setProgress((prev) => (prev === 0 ? 10 : prev));

      // Simulate incremental progress (slows down as it gets closer to 90)
      if (progressTimer) clearInterval(progressTimer);
      progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressTimer);
            return 90;
          }
          const increment = (90 - prev) * 0.15; // smooth deceleration
          return Math.min(90, prev + Math.max(1, increment));
        });
      }, 200);
    } else {
      // Completed! Fill to 100%
      if (visible) {
        setProgress(100);
        if (progressTimer) clearInterval(progressTimer);
        
        // Hold at 100% for animation to complete, then fade out
        fadeOutTimer = setTimeout(() => {
          setVisible(false);
          // Wait for fadeout opacity transition to finish, then reset progress to 0
          setTimeout(() => {
            setProgress(0);
          }, 300);
        }, 400);
      }
    }

    return () => {
      if (progressTimer) clearInterval(progressTimer);
      if (fadeOutTimer) clearTimeout(fadeOutTimer);
    };
  }, [activeRequests, visible]);

  useEffect(() => {
    // Intercept all requests
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        // Skip loader for background polling if specified (e.g. check-access permission polling)
        if (config.headers?.['X-Skip-Global-Loader']) {
          return config;
        }
        setActiveRequests((prev) => prev + 1);
        return config;
      },
      (error) => {
        // Handle error before sending
        if (error.config && !error.config.headers?.['X-Skip-Global-Loader']) {
          setActiveRequests((prev) => Math.max(0, prev - 1));
        }
        return Promise.reject(error);
      }
    );

    // Intercept responses/errors
    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        if (!response.config?.headers?.['X-Skip-Global-Loader']) {
          setActiveRequests((prev) => Math.max(0, prev - 1));
        }
        return response;
      },
      (error) => {
        if (!error.config?.headers?.['X-Skip-Global-Loader']) {
          setActiveRequests((prev) => Math.max(0, prev - 1));
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  if (!visible) return null;

  return (
    <div 
      className="fixed top-0 left-0 w-full z-[99999] pointer-events-none transition-opacity duration-300"
      style={{ opacity: progress === 100 ? 0 : 1 }}
    >
      <div 
        className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-[0_1px_12px_rgba(139,92,246,0.6)] transition-all duration-300 ease-out relative"
        style={{ width: `${progress}%` }}
      >
        {/* Glow point effect at the head of the progress bar */}
        <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-r from-transparent to-white opacity-80 animate-pulse" />
      </div>
    </div>
  );
}
